import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { readContract } from '@wagmi/core';
import { environment } from 'src/environments/environment';
import { getAddress } from 'viem';
import { ToastrService } from 'ngx-toastr';
import { AppkitService } from './appkit.service';
import { UserStateService } from './user-state.service';
import { TelegramErrorReporterService } from 'src/app/core/services/telegram-error-reporter.service';

const accessAbi = require('src/app/shared/abis/access.json');

// Shown when a KYC-verified user isn't yet registered on-chain (registration runs a few
// seconds after KYC approval), or when a transaction reverts with UserNotRegistered.
export const NOT_REGISTERED_MESSAGE =
  'Your account is being activated for on-chain transactions. This completes shortly after KYC approval - please try again in a few moments, or contact support if it persists.';

// Shown when the on-chain registration status can't be read (e.g. transient RPC error).
// Distinct from NOT_REGISTERED_MESSAGE: we don't know the user's status, so we ask them
// to retry rather than telling them they aren't registered.
export const VERIFY_FAILED_MESSAGE =
  'Unable to verify your account status right now. Please check your connection and try again.';

@Injectable({
  providedIn: 'root'
})
export class AccessControlContractService {

  // Throttle security reports so repeated blocked clicks from the same wallet don't spam the
  // backend/ops inbox. Per-session (resets on reload); the backend applies its own per-wallet throttle.
  private lastReportedAt = new Map<string, number>();
  private static readonly REPORT_COOLDOWN_MS = 60_000;

  constructor(
    private appkitService: AppkitService,
    private toastr: ToastrService,
    private http: HttpClient,
    private userState: UserStateService,
    private telegramReporter: TelegramErrorReporterService,
  ) { }

  async isBlocked(account: any) {
    return await readContract(this.appkitService.adapter.wagmiConfig, {
      address: await getAddress((environment as any)[account.chainId || environment.DEFAULT_NETWORK].ACCESS_CONTROL_CONTRACT),
      abi: accessAbi,
      chainId: Number(account.networkId),
      functionName: 'blockedUsers',
      args: [account.walletAddress]
    });
  }

  /** True if the wallet is registered in the on-chain platform whitelist (i.e. may transact). */
  async isRegistered(account: any) {
    return await readContract(this.appkitService.adapter.wagmiConfig, {
      address: await getAddress((environment as any)[account.chainId || environment.DEFAULT_NETWORK].ACCESS_CONTROL_CONTRACT),
      abi: accessAbi,
      chainId: Number(account.networkId),
      functionName: 'platformRegistered',
      args: [account.walletAddress]
    });
  }

  /**
   * Gate helper: returns true if the wallet is registered; otherwise shows a toast and
   * returns false so the caller can abort before sending a tx that would revert. Used
   * right after the isBlocked() gate:
   *   if (!(await this.accessControlContractService.ensureRegistered(this.account))) return;
   */
  async ensureRegistered(account: any, action?: string): Promise<boolean> {
    try {
      const registered = await this.isRegistered(account);
      if (!registered) {
        this.reportBlockedAttempt(account, action);
        this.toastr.error(NOT_REGISTERED_MESSAGE);
        return false;
      }
      return true;
    } catch (error) {
      // Read failed (e.g. transient RPC error). Fail closed - block the action and ask the
      // user to retry - so the FE gate matches the on-chain rule (the contract reverts with
      // UserNotRegistered) instead of optimistically letting a tx through that may revert.
      console.error('platformRegistered read failed', error);
      this.toastr.error(VERIFY_FAILED_MESSAGE);
      return false;
    }
  }

  /**
   * Notify the backend that a non-whitelisted wallet was blocked from transacting, so the team is
   * alerted with the attempter's details. Fire-and-forget and throttled - never blocks or throws
   * into the gate; errors are swallowed.
   */
  private reportBlockedAttempt(account: any, action?: string): void {
    const wallet = String(account?.walletAddress || '').toLowerCase();
    if (!wallet) return;

    const now = Date.now();
    const last = this.lastReportedAt.get(wallet) ?? 0;
    if (now - last < AccessControlContractService.REPORT_COOLDOWN_MS) return;
    this.lastReportedAt.set(wallet, now);

    // Alert ops directly too: the block happens in the browser, so the frontend is the only
    // place that knows which wallet was refused, on what action, and what the user was told.
    this.telegramReporter.report({
      type: 'SECURITY',
      service: 'Wallet whitelist',
      message: `Non-whitelisted wallet attempted a transaction: ${account.walletAddress}`,
      userMessage: NOT_REGISTERED_MESSAGE,
      context: {
        walletAddress: account.walletAddress,
        action: action ?? '-',
        userType: this.userState.regulated ? 'Regulated (custodial)' : 'Self-custody',
        walletProvider: account.walletProvider,
        chainId: account.chainId,
      },
    });

    this.http.post(`${environment.API_BASE_URL_V3}/report-blocked-attempt`, {
      wallet_address: account.walletAddress,
      chain_id: account.chainId,
      network_id: account.networkId,
      wallet_provider: account.walletProvider,
      action: action ?? null,
    }).subscribe({ error: () => { /* best-effort: must never affect the gate */ } });
  }
}
